import { DimensionDetails } from "../../interfaces/CoObject";
import { DimensionItem } from "./TreeAmountSelector";
import React, { useEffect, useState } from "react";
import { MultiSelectPills } from "../../generic/MultiSelectPills";
import { checkBeforeDelete, ModalSelectDimensionTree } from "./TreeInputModal";
import {
  CommandBarButton,
  Dropdown,
  Icon,
  IDropdownOption,
  IRenderFunction,
  ResponsiveMode,
  TooltipHost,
} from "@fluentui/react";
import { Toggle } from "@fluentui/react/lib/Toggle";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { ReferringScenarios, ReferringScenariosEnum } from "../../../api/models/ReferringScenarios";
import { isScenarioMetadata } from "../co-panel/ReferringScenarios";

interface TreeDropDownProps {
  label: string;
  placeholder: string;
  onChange: (values: (DimensionDetails | undefined)[]) => void;
  disabled?: boolean;
  rootOid: string;
  multiSelectable: boolean;

  hierarchical: boolean;
  dimensions: (DimensionDetails | undefined)[];
  onRemove?: () => void;
  modalTitle: string;

  fetchRoot: (parentOid: string) => Promise<DimensionItem[]>;
  fetchChildren: (parent: DimensionItem) => Promise<DimensionItem[]>;
  canInherit: DimensionDetails[] | undefined;
  inherited?: boolean;
  dbId: string;
  isSearchAvailable: boolean;
  referringScenarios?: ReferringScenarios;
}

const scenarioDropDownRender: (onRemove: () => void, disabled: boolean) => IRenderFunction<IDropdownOption<any>[]> =
  // eslint-disable-next-line react/display-name
  (onRemove: () => void, disabled) => (options: IDropdownOption[]) => {
    const option = options[0];
    return (
      <TooltipHost content={option.data?.tooltip} key={option.key}>
        <MultiSelectPills
          values={[
            {
              dimensionOid: option.key?.toString(),
              values: [
                {
                  oidPath: [],
                  oid: option.key?.toString(),
                  code: null,
                  desc: option.text,
                },
              ],
              inherited: false,
            },
          ]}
          onDelete={onRemove}
          disabled={disabled}
        />
      </TooltipHost>
    );
  };

export const TreeDropDownInput = ({
  placeholder,
  label,
  onChange,
  disabled,
  multiSelectable,
  hierarchical,
  rootOid,
  dimensions,
  onRemove,
  modalTitle,
  fetchRoot,
  fetchChildren,
  canInherit,
  inherited,
  dbId,
  isSearchAvailable,
  referringScenarios,
}: TreeDropDownProps) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  const onChangeRef = (newRef: string | undefined) => {
    onChange(
      dimensions.map((d) => ({
        ...d,
        values: d.values.map((v) => ({
          ...v,
          ref: newRef as ReferringScenariosEnum | undefined,
        })),
      }))
    );
    if (!newRef) setRefOptions((s) => ({ ...s, visible: false }));
  };

  const [refOptions, setRefOptions] = useState<{ enabled: boolean; visible: boolean }>({
    enabled: false,
    visible: false,
  });

  useEffect(() => {
    const hasRef = dimensions?.[0]?.values?.[0]?.ref != undefined;
    setRefOptions({
      visible: hasRef,
      enabled: referringScenarios && inherited && (hasRef || isScenarioMetadata(canInherit?.[0])),
    });
  }, [referringScenarios, inherited, canInherit]);

  return (
    <div style={{ display: "flex", flexDirection: "column", paddingTop: "5" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
        <span className="bodyText">{label}</span>
        {onRemove ? <span className="k-icon k-i-close" onClick={onRemove} /> : null}
      </div>
      {canInherit ? (
        <div style={{ justifyContent: "space-between", alignItems: "center" }}>
          <Toggle
            label={formatMessage("cof.documentproperties.inherit")}
            inlineLabel
            checked={inherited ?? null}
            onChange={(_, checked) => {
              if (checked) onChange(canInherit.map((v) => ({ ...v, inherited: true })));
              else onChange([]);
            }}
            styles={{ root: { marginBottom: 0 } }}
            disabled={(!canInherit.length && !inherited) || disabled}
            onText=" "
            offText=" "
          />
        </div>
      ) : null}
      <div
        style={{
          border: "1px solid var(--color-black)",
          borderRadius: "4px",
          display: "flex",
          paddingLeft: "0.25rem",
          paddingBottom: "5",
          backgroundColor: "white",
        }}
      >
        <MultiSelectPills
          id={modalTitle}
          placeholder={placeholder}
          onDelete={(valueToDelete) => {
            const newDimensions = checkBeforeDelete(dimensions, valueToDelete);
            onChange(newDimensions);
          }}
          onClick={() => !inherited && !disabled && setModalOpen(true)}
          disabled={disabled || inherited}
          values={dimensions}
        />
        {inherited || disabled ? null : (
          <Icon
            data-testid="More"
            id={modalTitle}
            iconName="More"
            style={{ margin: "auto 0.5rem", cursor: "pointer" }}
            onClick={() => setModalOpen(true)}
          />
        )}
      </div>
      {refOptions.enabled && !disabled && (
        <CommandBarButton
          text={refOptions.visible ? formatMessage("cof.base.hideoptions") : formatMessage("cof.base.showoptions")}
          onClick={() => setRefOptions((s) => ({ ...s, visible: !s.visible }))}
          styles={{
            flexContainer: { width: "100%", padding: "5px 0 5px 0" },
            rootHovered: { backgroundColor: "transparent" },
            rootPressed: { backgroundColor: "transparent" },
            label: { textAlign: "right", textDecoration: "underline", color: "var(--color-blue)" },
          }}
        />
      )}
      {refOptions.enabled && refOptions.visible && (
        <Dropdown
          styles={{ root: { marginTop: "5px" }, title: { padding: "0 0 0 4px" } }}
          responsiveMode={ResponsiveMode.large}
          options={
            referringScenarios?.referenceScenarios?.map(
              (d) =>
                ({
                  key: d.reference,
                  text: formatMessage(`cof.createnewobject.referringscenario.${d.reference.toLowerCase()}`),
                  data: { tooltip: d.description },
                } as any)
            ) ?? []
          }
          onRenderTitle={scenarioDropDownRender(() => onChangeRef(null), disabled)}
          selectedKey={dimensions?.[0]?.values?.[0]?.ref}
          disabled={disabled}
          placeholder={
            referringScenarios?.referenceScenarios?.length
              ? formatMessage("cof.base.select")
              : formatMessage("cof.base.nooptionsavailable")
          }
          onChange={(_, opt) => {
            onChangeRef(opt?.key.toString());
          }}
        />
      )}
      {!disabled && isModalOpen ? (
        <ModalSelectDimensionTree
          onlyOneValue={multiSelectable === false}
          isModalOpen={true}
          setModalOpen={setModalOpen}
          onChange={(newDimensions) => {
            onChange(newDimensions);
          }}
          fetchRoot={fetchRoot}
          fetchChildren={fetchChildren}
          dimensions={
            dimensions
              ? dimensions.map((d) => ({
                  //cloning values, needed for keeping changes unsaved
                  values: d.values.map((v) => ({ ...v })),
                  hierarchy: d.hierarchy,
                  dimensionOid: d.dimensionOid,
                  inherited: d.inherited,
                  label: d.label,
                }))
              : undefined
          }
          title={modalTitle}
          rootOid={rootOid}
          hierarchical={hierarchical}
          dbId={dbId}
          isSearchAvailable={isSearchAvailable}
        />
      ) : null}
    </div>
  );
};
